/**
 * HPQ Local Cart — Write-behind cart state
 *
 * Architecture: git model
 *   Working tree = hpqCart (all UI reads from here, instant)
 *   Commit       = checkout click (syncNow → push to Shopify)
 *   Push         = background sync (lazy, fire-and-forget)
 *   Pull         = hydrate (page load, BFCache)
 *
 * UI never waits on the network. Shopify is the "remote" — kept in sync
 * for checkout, but never the source of truth for display.
 */
(function () {
  'use strict';

  var SYNC_DELAY = 80;        // ms debounce — catches double-clicks, otherwise near-instant
  var syncTimer = null;
  var syncing = false;
  var dirty = false;           // local state has unsaved changes
  var mutationCount = 0;       // tracks setQty calls — detects mid-sync changes
  var _hydratePromise = null;  // dedup concurrent hydrate() calls

  // ── Local cart state ─────────────────────────────────────────────
  // items: { variantId: { qty, price, title, key, image, url, variant_title } }
  var localCart = {
    items: {},
    itemCount: 0,
    totalPrice: 0,             // LOCAL sum of qty*price = PRE-discount subtotal (instant)
    // Server-reconciled POST-discount total. Automatic discounts (the 3+ volume
    // discounts) are computed by Shopify, not knowable locally, so the free-shipping
    // threshold — which must match checkout — has to key off this, never totalPrice.
    // null until the first hydrate/sync completes.
    serverTotalPrice: null,
    serverTotalDiscount: 0,
    loaded: false,
    serverVids: []             // variant IDs last known on the server
  };

  // ── Public API (window.hpqCart) ──────────────────────────────────

  window.hpqCart = {
    // Get quantity for a variant (instant, 0ms)
    getQty: function (variantId) {
      var item = localCart.items[String(variantId)];
      return item ? item.qty : 0;
    },

    // Set quantity for a variant (instant, 0ms, schedules background sync)
    // meta: optional { image, url, variant_title }
    setQty: function (variantId, qty, price, title, meta) {
      variantId = String(variantId);
      qty = Math.max(0, qty);

      if (qty === 0) {
        delete localCart.items[variantId];
      } else {
        if (!localCart.items[variantId]) {
          localCart.items[variantId] = {
            qty: 0, price: price || 0, title: title || '', key: '',
            image: '', url: '', variant_title: ''
          };
        }
        localCart.items[variantId].qty = qty;
        if (price !== undefined && price !== null) localCart.items[variantId].price = price;
        if (title) localCart.items[variantId].title = title;
        if (meta) {
          if (meta.image) localCart.items[variantId].image = meta.image;
          if (meta.url) localCart.items[variantId].url = meta.url;
          if (meta.variant_title !== undefined) localCart.items[variantId].variant_title = meta.variant_title;
        }
      }

      recalcTotals();
      dirty = true;
      mutationCount++;
      broadcastChange();
      scheduleSyncToShopify();
    },

    // Add delta to quantity (instant)
    addQty: function (variantId, delta, price, title, meta) {
      var current = this.getQty(variantId);
      this.setQty(variantId, current + delta, price, title, meta);
    },

    // Get total item count
    getItemCount: function () { return localCart.itemCount; },

    // Get all items as array (includes metadata for drawer rendering)
    getItems: function () {
      var result = [];
      for (var vid in localCart.items) {
        if (localCart.items.hasOwnProperty(vid)) {
          var it = localCart.items[vid];
          result.push({
            variant_id: vid,
            quantity: it.qty,
            price: it.price,
            title: it.title,
            key: it.key,
            image: it.image || '',
            url: it.url || '',
            variant_title: it.variant_title || ''
          });
        }
      }
      return result;
    },

    // Get cart-like object for compatibility
    getCart: function () {
      // Return an INTERNALLY CONSISTENT snapshot (original_total_price − total_discount ===
      // total_price). Mixing a freshly-recalculated local subtotal with a stale server
      // total/discount produced impossible rows (Subtotal $55.96 − Savings $6.00 ≠ Total $35.97)
      // on any broadcast not followed by the drawer's tier-aware optimistic dispatch (e.g. a PDP
      // add-to-cart). While dirty (or before the first hydrate) we don't yet know the discounted
      // total, so present a self-consistent optimistic cart (pre-discount, no savings) until sync
      // reconciles. When clean, use the server generation and derive original = total + discount.
      var subtotalLocal = localCart.totalPrice;
      var totalPrice, totalDiscount, originalTotal;
      if (dirty || localCart.serverTotalPrice == null) {
        totalPrice = subtotalLocal;
        totalDiscount = 0;
        originalTotal = subtotalLocal;
      } else {
        totalPrice = localCart.serverTotalPrice;
        totalDiscount = localCart.serverTotalDiscount || 0;
        originalTotal = totalPrice + totalDiscount;
      }
      return {
        item_count: localCart.itemCount,
        total_price: totalPrice,                      // POST-discount (checkout-matching) when synced
        items_subtotal_price: subtotalLocal,          // local PRE-discount subtotal (instant)
        original_total_price: originalTotal,          // consistent: == total_price + total_discount
        total_discount: totalDiscount,
        items: this.getItems().map(function (i) {
          return {
            variant_id: parseInt(i.variant_id, 10),
            quantity: i.quantity, price: i.price, title: i.title,
            key: i.key, image: i.image, url: i.url, variant_title: i.variant_title
          };
        })
      };
    },

    // Is the local state dirty (unsaved changes)?
    isDirty: function () { return dirty; },

    // Force sync now (used before checkout)
    syncNow: function () {
      return syncToShopify();
    },

    // Initial hydration from Shopify (deduped — safe to call multiple times)
    hydrate: function () {
      if (_hydratePromise) return _hydratePromise;
      _hydratePromise = fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          hydrateFromShopify(cart);
          broadcastChange();
        })
        .catch(function () {})
        .then(function () { _hydratePromise = null; });
      return _hydratePromise;
    },

    // Is loaded?
    isLoaded: function () { return localCart.loaded; }
  };

  // ── Internal ─────────────────────────────────────────────────────

  function recalcTotals() {
    var count = 0;
    var total = 0;
    for (var vid in localCart.items) {
      if (localCart.items.hasOwnProperty(vid)) {
        count += localCart.items[vid].qty;
        total += localCart.items[vid].qty * localCart.items[vid].price;
      }
    }
    localCart.itemCount = count;
    localCart.totalPrice = total;
  }

  function hydrateFromShopify(cart) {
    // Guard against a throttled/errored /cart.js (e.g. a 429 returning {status:429}): a response
    // without a numeric total_price and an items array is not a cart. Bail rather than reset items
    // to {} while leaving a stale positive serverTotalPrice — which would report 0 items with a
    // non-zero total (and a false "free shipping earned"). The next good fetch hydrates cleanly.
    if (!cart || typeof cart.total_price !== 'number' || !Array.isArray(cart.items)) return;
    localCart.items = {};
    localCart.serverVids = [];
    // Capture Shopify's authoritative POST-discount total (includes the automatic
    // volume discounts). This is the number checkout will use for the free-ship
    // threshold — the cart must show the same, so the free-ship bars key off it.
    if (cart && typeof cart.total_price === 'number') {
      localCart.serverTotalPrice = cart.total_price;
      localCart.serverTotalDiscount = (typeof cart.total_discount === 'number') ? cart.total_discount : 0;
    }
    if (cart && cart.items) {
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var vid = String(item.variant_id);
        localCart.items[vid] = {
          qty: item.quantity,
          price: item.price,
          title: item.title,
          key: String(item.key),
          image: item.image || '',
          url: item.url || '',
          variant_title: item.variant_title || ''
        };
        localCart.serverVids.push(vid);
      }
    }
    recalcTotals();
    localCart.loaded = true;
    dirty = false;
  }

  // Update serverVids without overwriting local items (used when mid-sync changes detected)
  function updateServerVids(cart) {
    localCart.serverVids = [];
    if (cart && cart.items) {
      for (var i = 0; i < cart.items.length; i++) {
        localCart.serverVids.push(String(cart.items[i].variant_id));
      }
    }
  }

  function broadcastChange() {
    // Update all header cart badges instantly
    var badges = document.querySelectorAll('[data-js-cart-count]');
    for (var i = 0; i < badges.length; i++) badges[i].textContent = localCart.itemCount;

    // Dispatch event for other components (PDP stepper, flavor badges, etc.)
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: window.hpqCart.getCart(), source: 'hpq-local-cart' }
    }));
  }

  // ── Background sync to Shopify (write-behind) ────────────────────

  function scheduleSyncToShopify() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () { syncToShopify(); }, SYNC_DELAY);
  }

  function syncToShopify() {
    if (!dirty && !syncing) return Promise.resolve();
    if (syncing) {
      // Sync in flight — return a promise that resolves when it finishes (10s cap)
      return new Promise(function (resolve) {
        var elapsed = 0;
        var poll = setInterval(function () {
          elapsed += 50;
          if (!syncing || elapsed > 10000) { clearInterval(poll); resolve(); }
        }, 50);
      });
    }
    syncing = true;

    // Diff local state against last-known server state
    var updates = {};     // existing items: qty changes + removals
    var newItems = [];    // items not yet on server: need /cart/add.js

    for (var vid in localCart.items) {
      if (localCart.items.hasOwnProperty(vid)) {
        if (localCart.serverVids.indexOf(vid) !== -1) {
          updates[vid] = localCart.items[vid].qty;
        } else {
          newItems.push({ id: parseInt(vid, 10), quantity: localCart.items[vid].qty });
        }
      }
    }

    // Items on server but NOT in local state — removed, set to 0
    for (var i = 0; i < localCart.serverVids.length; i++) {
      var svid = localCart.serverVids[i];
      if (!localCart.items[svid]) {
        updates[svid] = 0;
      }
    }

    var hasUpdates = false;
    for (var k in updates) { if (updates.hasOwnProperty(k)) { hasUpdates = true; break; } }

    // Step 1: Add new items (if any)
    var addStep = newItems.length > 0
      ? fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: newItems })
        }).then(function (r) { if (!r.ok) throw new Error(r.status); })
      : Promise.resolve();

    // Snapshot mutation count to detect mid-sync user changes
    var syncMutations = mutationCount;

    // Step 2: Update quantities + remove items
    return addStep
      .then(function () {
        if (!hasUpdates) {
          return fetch('/cart.js').then(function (r) { return r.json(); });
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (res) {
          if (!res.ok) throw new Error(res.status);
          return res.json();
        });
      })
      .then(function (cart) {
        if (mutationCount === syncMutations) {
          // No mid-sync changes — safe to reconcile fully from server
          hydrateFromShopify(cart);
          dirty = false;
        } else {
          // User made changes during sync — preserve their local state,
          // just update serverVids so next sync diffs correctly
          updateServerVids(cart);
          // dirty stays true → re-sync fires with the user's latest state
        }

        document.dispatchEvent(new CustomEvent('hpq:cart-synced'));
        broadcastChange();
      })
      .catch(function () {
        if (mutationCount === syncMutations) {
          // No mid-sync changes — safe to re-hydrate from server
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              hydrateFromShopify(cart);
              broadcastChange();
            })
            .catch(function () { localCart.serverVids = []; });
        } else {
          // User made changes — preserve their state, update serverVids
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (c) { updateServerVids(c); })
            .catch(function () { localCart.serverVids = []; });
        }
      })
      .then(function () {
        syncing = false;
        if (dirty) scheduleSyncToShopify();
      });
  }

  // ── Checkout gate: ensure sync before navigating ─────────────────

  function draftCheckoutActive() {
    return (!!(window.HPQ_CONFIG && window.HPQ_CONFIG.draftCheckoutEnabled === true) ||
      window.HPQ_FORCE_DRAFT_CHECKOUT === true) && !!window.hpqDraftCheckout;
  }

  // Immediate feedback for the draft path: the worker round-trip + Shopify's draft->checkout
  // redirect take ~1-2s, during which a bare button looks frozen (and a slow worker holds it
  // up to the fallback timeout). Show a spinner (CSS in theme-globals.liquid) and block
  // re-clicks — a UI complement to the draft module's one-shot latch. Only the draft path
  // ever sets this, so the native (flag-off) path is untouched.
  function setCheckoutLoading(on) {
    var btns = document.querySelectorAll('button[name="checkout"], a[href*="/checkout"], input[name="checkout"]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      b.classList.toggle('hpq-checkout-loading', on);
      if (on) {
        b.setAttribute('aria-busy', 'true');
        b.setAttribute('disabled', 'disabled');
        // Inject a dedicated spinner element rather than a ::after pseudo — the theme already
        // uses ::after on the checkout button (a full-width fill), which would override ours.
        if (b.tagName !== 'INPUT' && !b.querySelector('.hpq-checkout-spinner')) {
          var s = document.createElement('span');
          s.className = 'hpq-checkout-spinner';
          s.setAttribute('aria-hidden', 'true');
          b.appendChild(s);
        }
      } else {
        b.removeAttribute('aria-busy');
        b.removeAttribute('disabled');
        var ex = typeof b.querySelector === 'function' ? b.querySelector('.hpq-checkout-spinner') : null;
        if (ex && ex.parentNode) ex.parentNode.removeChild(ex);
      }
    }
  }

  // Shared routing for both a checkout click and an Enter-to-submit on the cart form. When
  // the draft flow is on we intercept EVERY checkout intent — even a clean cart must go
  // through the worker draft (that's what keeps codes cart-only and shipping consistent) —
  // so we can't take the native early-return. allowNativeEarlyReturn preserves today's
  // clean-cart passthrough for the native (flag-off) click path byte-for-byte.
  function routeCheckout(e, allowNativeEarlyReturn) {
    var draftOn = draftCheckoutActive();
    if (!draftOn && allowNativeEarlyReturn && !dirty && !syncing) return;

    e.preventDefault();
    e.stopPropagation();

    // Draft path only: show the spinner the moment the shopper commits, before the async
    // sync + worker round-trip. Navigation (success or native fallback) unloads the page;
    // the one non-navigating outcome (422 reject) resets it via the listener below.
    if (draftOn) setCheckoutLoading(true);

    // Sync first so the draft/native checkout reflects the latest cart. syncToShopify() on a
    // clean cart is a cheap /cart.js re-read, so this is safe to always await.
    var proceed = function () {
      if (draftOn && window.hpqDraftCheckout) {
        var codes = (typeof window.hpqDraftCheckout.getCodes === 'function')
          ? window.hpqDraftCheckout.getCodes() : [];
        window.hpqDraftCheckout.go({ appliedCodes: codes });
      } else {
        window.location.href = '/checkout';
      }
    };
    // Draft flow: proceed even if the pre-checkout sync rejects (the draft module has its own
    // native fallback), so a transient sync error never leaves the checkout button dead. The
    // native flow keeps its original single-path behavior byte-for-byte.
    if (draftOn) { syncToShopify().then(proceed, proceed); }
    else { syncToShopify().then(proceed); }
  }

  document.addEventListener('click', function (e) {
    var checkoutLink = e.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
    if (!checkoutLink) return;
    routeCheckout(e, true);
  }, true);

  // The control the browser submitted with. Prefer SubmitEvent.submitter (the clicked button, or
  // for an Enter-key submit the form's default button). When submitter is unsupported (older
  // Safari) or unset, fall back to the form's DEFAULT submit control — the first submit-type
  // button in tree order, which is exactly what the browser submits with — so we never
  // misidentify Update as Checkout.
  function defaultSubmitName(form) {
    var controls = form.querySelectorAll('button, input[type="submit"], input[type="image"]');
    for (var i = 0; i < controls.length; i++) {
      var c = controls[i];
      if (c.disabled) continue;
      var type = (c.getAttribute('type') || (c.tagName === 'BUTTON' ? 'submit' : '')).toLowerCase();
      if (type === 'submit' || type === 'image') return c.getAttribute('name');
    }
    return null;
  }

  // Enter-to-submit on the cart form (or any form whose submit is the checkout button) would
  // otherwise bypass the click gate and reach native checkout with the coupon dropped. Only
  // intercept when the draft flow is on, so the native flow is unchanged.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || typeof form.querySelector !== 'function') return;
    if (!form.querySelector('[name="checkout"]')) return;
    // The cart form holds BOTH a Checkout and an Update submit button. Route to draft checkout
    // ONLY for the Checkout submit; Update (and an Enter whose default submit is Update) must
    // stay native. Resolve the submitter robustly so browsers without SubmitEvent.submitter
    // don't fall through and hijack Update.
    var submitterName = e.submitter ? e.submitter.getAttribute('name') : defaultSubmitName(form);
    if (submitterName !== 'checkout') return;
    if (!draftCheckoutActive()) return;
    routeCheckout(e, false);
  }, true);

  // A rejected code keeps the shopper on the cart (the draft module dispatches this instead
  // of navigating), so clear the checkout spinner and re-enable the button for a retry.
  document.addEventListener('hpq:draft-code-rejected', function () { setCheckoutLoading(false); });

  // ── BFCache support ──────────────────────────────────────────────

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      // Back-button/bfcache restore freezes the DOM as it was at navigation — including the
      // checkout button left disabled + spinnered by setCheckoutLoading(true) before a draft
      // redirect. Clear it so checkout is usable again (the draft module resets its own latch).
      setCheckoutLoading(false);
      window.hpqCart.hydrate();
    }
  });

  // ── External cart changes ──────────────────────────────────────────

  document.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.source === 'hpq-local-cart') return;
    var cart = e.detail && e.detail.cart;
    if (cart && cart.items) {
      hydrateFromShopify(cart);
    }
  });

  // ── Auto-hydrate on page load ──────────────────────────────────────
  window.hpqCart.hydrate();

})();
